<template>
  <div class="app">
    <cl-sidebar>
      <template #company-logo>
        <t-logo @click.prevent="redirectBasedOnRole" />
      </template>
      <template #dropdown-select="{ isClosed }">
        <t-nav-dropdown
          icon="cl-tabler-icon-users"
          data-test-id="navbar-msp-select"
          :loading="false"
          :is-closed="isClosed"
          :options="userAccountList"
          :initial-selected="initialMsp"
          @reset-list="setDropDownList"
          @search="itemBeingSearched"
          @selected="customerSelected"
        />
      </template>
      <template #side-nav="{ isClosed: isSidebarCollapsed }">
        <t-side-nav
          v-if="!loadingSolutions"
          :nav-menu-set-up="mspNavMenuSetUp"
          :is-sidebar-collapsed="isSidebarCollapsed"
          :tier="tier"
        ></t-side-nav>
      </template>
    </cl-sidebar>
    <div class="app-content">
      <t-upper-nav />
      <main
        class="tw-overflow-y-aut tw-flex-1 tw-bg-light tw-p-4 max-lg:tw-ml-14"
      >
        <div class="tw-w-full">
          <template v-if="isRbacAvailable">
            <read-only-role-banner
              :visible="isReadOnlyRoleVisible"
              level="msp"
            />
          </template>

          <t-page-header
            :product-links="productLinks"
            :user-links="userLinks"
            :data-test-id="'header-user-menu'"
          />
          <router-view />
        </div>
      </main>
      <cl-footer />
    </div>
  </div>
</template>

<script>
import { ref, onBeforeMount } from "vue";
import { useStore, mapActions, mapGetters } from "vuex";
import { useRouter, useRoute } from "vue-router";

import TPageHeader from "@/components/TPageHeader";
import TNavDropdown from "@/components/TNavDropdown";
import { TIER } from "@/constants";

import {
  usePrimaryNavItems,
  useEnabledSolutions,
  useRedirectBasedOnRole,
} from "@/composables";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import { useFlag } from "@/composables/useFlags/useFlag";

import { NAV_ITEMS_MODE } from "@/constants";
import { ALL_SERVICES } from "@/utils/services";

import {
  newFeaturePageVisitedCookie,
  getSideNavItemPill,
} from "@/components/TNewFeaturesModal/utils/new-features-announcement.ts";
import TSideNav from "@/components/TSideNav";
import TLogo from "@/components/TLogo/TLogo.vue";
import TUpperNav from "@/components/TUpperNav/TUpperNav.vue";
import ReadOnlyRoleBanner from "@/views/Common/ReadOnlyRoleBanner.vue";

export default {
  name: "MspLayout",
  components: {
    ReadOnlyRoleBanner,
    TNavDropdown,
    TPageHeader,
    TSideNav,
    TLogo,
    TUpperNav,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const { navItems } = usePrimaryNavItems();
    const { enabledSolutions } = useEnabledSolutions();
    const { mspGlobalOrganisationId } = useGlobalOrganisationID();
    const { isFeatureAvailable } = useFlag("FEATURE_TOUR");
    const { isFeatureAvailable: isRbacAvailable } = useFlag("RBAC");
    const redirectBasedOnRole = useRedirectBasedOnRole();

    const mspNavItems = navItems.get(NAV_ITEMS_MODE.MSP);
    const loadingSolutions = ref(true);

    const getSolutions = async (uuid) => {
      await store.dispatch("solutions/getSolutions", {
        uuid,
      });
    };

    onBeforeMount(async () => {
      try {
        loadingSolutions.value = true;

        store.dispatch("solutions/resetState");
        await getSolutions(mspGlobalOrganisationId.value);
      } finally {
        if (route.meta.solutionId) {
          let isSolutionDisabled = false;

          isSolutionDisabled = !enabledSolutions.value.some(
            (solution) => solution.solution_uuid === route.meta.solutionId,
          );

          if (isSolutionDisabled) {
            await router.replace({ name: "msp-root" });
            router.go(0);
          }
        }

        loadingSolutions.value = false;
      }
    });

    return {
      loadingSolutions,
      enabledSolutions,
      mspNavItems,
      isFeatureAvailable,
      isRbacAvailable,
      redirectBasedOnRole,
    };
  },
  data() {
    return {
      userAccountList: null,
      initialMsp: null,
      tier: TIER.msps,
      params: {
        filter: "",
        results: 20,
      },
      productLinks: [],
      userLinks: [],
    };
  },
  computed: {
    ...mapGetters("authentication", [
      "selectedMsp",
      "selectedAccount",
      "authUser",
      "mspId",
      "role",
    ]),
    ...mapGetters("dropDown", ["dropDownList"]),
    isPillVisible() {
      return this.isFeatureAvailable && !newFeaturePageVisitedCookie.value;
    },
    isReadOnlyRoleVisible() {
      return this.authUser.role === "admin-ro";
    },
    mspNavMenuSetUp() {
      const routePath = this.$route.path;
      const setIsActive = (link) => routePath.includes(link);

      const items = this.mspNavItems
        .map((item) => {
          const serviceSettings = ALL_SERVICES.find(
            (service) => service.uuid === item.solutionId,
          );

          let isSolutionDisabled = false;
          let tooltip = null;

          if (item.solutionId) {
            isSolutionDisabled = !this.enabledSolutions.some(
              (solution) => solution.service.uuid === item.solutionId,
            );
          }

          // Instead of hiding, the disabled services will appear for MSPs:
          // https://gitlab.titanhq.com/titanhq/dev/scrum/team-platfarmers/-/issues/2097
          if (isSolutionDisabled) {
            tooltip = {
              content: this.$t(serviceSettings.tooltipLabelUnavailable),
              placement: "top",
            };
          }

          return {
            ...item,
            tooltip,
            disabled: isSolutionDisabled,
            pill:
              item.status && this.isPillVisible && !setIsActive(item.id)
                ? getSideNavItemPill(item.status)
                : "",
            children: item.children?.map((child) => ({
              ...child,
              disabled: isSolutionDisabled,
              pill:
                child.status && this.isPillVisible
                  ? getSideNavItemPill(child.status)
                  : "",
              children: child.children?.map((grandChild) => ({
                ...grandChild,
                disabled: isSolutionDisabled,
                pill:
                  grandChild.status && this.isPillVisible
                    ? getSideNavItemPill(grandChild.status)
                    : "",
              })),
            })),
          };
        })
        .filter(Boolean);

      return this.separatedByGroup(items);
    },
  },
  watch: {
    dropDownList: {
      handler() {
        this.userAccountList = this.cleanAccounts(this.dropDownList);
      },
      deep: true,
      immediate: true,
    },
  },
  beforeMount() {
    let parentPath = "";

    if (this.selectedAccount?.type_name === "System") {
      parentPath += "/system";
    }

    this.setParentPath(parentPath);

    this.setMSP(this.selectedMsp?.name || "");
  },
  async created() {
    this.setSupportLinks();
    this.setCustomer(null); // resets customer if users hits the back arrow
  },

  methods: {
    ...mapActions("pageMeta", ["setParentPath", "setMSP"]),
    ...mapActions("authentication", ["setCustomer", "selectedMsp"]),
    ...mapActions("dropDown", ["getDropDownList", "resetState"]),

    separatedByGroup(mspArray) {
      const mspByGroup = {};
      mspArray.forEach((item) => {
        if (mspByGroup[item.groupBy]) {
          mspByGroup[item.groupBy].push(item);
        } else {
          mspByGroup[item.groupBy] = [item];
        }
      });

      return mspByGroup;
    },
    setSupportLinks() {
      const id = this.authUser?.uuid;

      this.userLinks = [
        {
          title: this.$t("Support"),
          link: "https://support.titanhq.com/en/69450-welcome-to-the-titanhq-platform-.html",
          target: "_blank",
          icon: "cl-icon-tabler-lifebuoy",
        },
        {
          title: this.$t("Security"),
          link: `/profile/security?id=${id}`,
          target: "_blank",
          icon: "cl-icon-tabler-settings",
        },
      ];
    },
    async customerSelected(customer) {
      this.setCustomer(customer);
      if (customer.allMsps && this.selectedAccount?.type_name === "System") {
        this.$router.push("/system/dashboard").catch(() => {});
      } else if (customer.allCustomers) {
        this.$router.push("/msp/customers");
      } else if (
        customer.allMsps &&
        this.selectedAccount?.type_name === "MSP"
      ) {
        this.$router.push("/msp/customers");
      } else if (this.$route.path !== "/customer/email_security/overview") {
        this.$router.push({ name: "customer-email_security" });
      }
    },
    async setDropDownList() {
      try {
        this.resetState();
        await this.getDropDownList({
          globalOrganisationID:
            this.selectedMsp?.uuid || this.selectedAccount?.uuid,
          params: this.params,
        });
      } finally {
        this.userAccountList = this.cleanAccounts(this.dropDownList);
      }
    },
    async itemBeingSearched(item) {
      try {
        const params = {
          filter: item,
          results: 20,
        };
        this.resetState();
        await this.getDropDownList({
          globalOrganisationID:
            this.selectedMsp?.uuid || this.selectedAccount?.uuid,
          params: params,
        });
      } finally {
        this.loading = false;
      }
    },
    cleanAccounts(accounts) {
      const allAccounts = accounts
        .map((acct) => {
          return {
            ...acct,
            id: acct.uuid,
            name: acct.name || "",
            short: this.$getAccountShortName(acct.name || ""),
            active: false,
            allCustomers: false,
            placeholder: "Search Customers",
          };
        })
        .filter((res) => res.organisation_type === "Customer")
        .sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase()
            ? 1
            : b.name.toLowerCase() > a.name.toLowerCase()
              ? -1
              : 0,
        );

      if (this.selectedAccount?.type_name === "System") {
        let otherAccounts = [
          {
            name: this.$t("menu-item-all-msps"),
            short: "AM",
            allMsps: true,
            allCustomers: false,
            placeholder: "Search MSPs",
          },
        ];
        return [...otherAccounts, ...allAccounts];
      } else {
        let otherAccounts = [
          {
            name: "All Customers",
            short: "AM",
            allMsps: false,
            allCustomers: true,
            placeholder: "Search Customers",
          },
        ];
        return [...otherAccounts, ...allAccounts];
      }
    },
  },
};
</script>
